import { useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { STEPS } from "./onboardingModel";
import { mergeItems } from "@/components/intake/intakeModel";
import { previewOutcome } from "./preview/previewModel";

export const GREETINGS = {
  identify: "Hi — I'm your setup guide. Drop your website and I'll do the reading, or just tell me in your own words what you sell and to whom.",
  confirm: "Here's what I pulled from your site. Tell me what's off — \"we don't sell to banks\", \"our buyer is the COO, not the CFO\" — and I'll fix the form.",
  customers: "Who do you already work with, and who's on your wishlist? Name a few companies or describe them; I'll sort customers from targets.",
  targeting: "Three things only you know: your best customers, who to avoid, and where/how big. Say it naturally — \"US only, 50–500 people, skip recruiters\".",
  preview: "These are live samples from your targeting. Too broad? Wrong titles? Tell me and I'll tighten the spec before you commit.",
  commit: "Last part: the number. What's the revenue goal, by when, and what does winning look like for you this year?",
};

const SUGGESTIONS = {
  identify: ["We sell to…", "What should I expect?", "I have no website"],
  confirm: ["Our buyer is actually…", "Remove an industry", "What's an entity type?"],
  customers: ["Our top customers are…", "We'd love to win…", "Why does this matter?"],
  targeting: ["Only US and Canada", "Companies with 50–500 people", "Never target competitors like…"],
  preview: ["These are too small", "Wrong job titles", "Looks right"],
  commit: ["$1M by December", "Our mission is…", "What's a realistic deal size?"],
};

const describe = (patches, items) => {
  const parts = [...Object.keys(patches.spec || {}), ...Object.keys(patches.data || {})];
  if (items?.length) parts.push(`${items.length} companies staged`);
  return parts;
};

export const useOnboardingAssistant = ({ userId, step, data, patch, patchSpec }) => {
  const stepId = STEPS[step]?.id || "identify";
  const chat = data.chat || {};
  const [busy, setBusy] = useState(false);
  const thread = chat[stepId] || [{ role: "assistant", content: GREETINGS[stepId] || GREETINGS.targeting, suggestions: SUGGESTIONS[stepId] || [] }];

  const persist = useCallback((stepThread) => patch((prev) => ({ chat: { ...(prev.chat || {}), [stepId]: stepThread } })), [patch, stepId]);

  const applyPatches = useCallback((patches, items) => {
    const d = { ...(patches.data || {}) };
    if (items?.length) d.intakeItems = mergeItems(data.intakeItems || [], items);
    if (Object.keys(d).length) patch(d);
    const spec = { ...(patches.spec || {}) };
    if (d.bestCustomers && !spec.lookalikeSeeds) spec.lookalikeSeeds = d.bestCustomers;
    if (Object.keys(spec).length) patchSpec(spec);
  }, [data.intakeItems, patch, patchSpec]);

  const send = async (text) => {
    const content = text.trim();
    if (!content || busy) return;
    const base = [...thread, { role: "user", content }];
    persist(base);
    setBusy(true);
    try {
      const { chat: _c, preview, huntJobId, analysis, ...draft } = data;
      const { data: res } = await axios.post(`${API}/onboarding/assistant/${userId}`, {
        step: stepId, draft: { ...draft, analysis: { sellerIntelligence: analysis?.sellerIntelligence }, previewOutcome: stepId === "preview" ? previewOutcome(preview) : null },
        messages: base.map(({ role, content: c }) => ({ role, content: c })),
      }, { timeout: 60000 });
      applyPatches(res.patches || {}, res.intakeItems);
      persist([...base, { role: "assistant", content: res.reply, applied: describe(res.patches || {}, res.intakeItems), suggestions: res.suggestions || [] }]);
    } catch (e) {
      persist([...base, { role: "assistant", content: "Something went wrong on my side — try again in a moment." }]);
    } finally { setBusy(false); }
  };

  return { thread, send, busy, stepId };
};
